import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	TextField,
	Link as ForgotLink,
	Typography
} from '@mui/material'
import { Box, styled } from '@mui/system'
import dayjs from 'dayjs'
import i18n from 'i18next'
import React from 'react'
import { Cookies } from 'react-cookie'
import { useForm, Controller } from 'react-hook-form'
import { getLangLink } from '../../helpers'
import { useDefaultComponentTools } from '../../hooks/useDefaultComponentTools'
import { setShowLoginPopup, setToken, signInAction } from '../../store/redusers/auth/actions'
import { getSocket } from '../../store/redusers/sockets/actions'
import AppButton from '../AppButton'


const FormLoginBody = styled(Box)(() => ({
	'>form': {
		display: 'flex',
		flex: 1,
		flexDirection: 'column'
	},
	display: 'flex',
	flexDirection: 'column',
	padding: ' 0 16px'
}))


const USERNAME_FIELD_NAME = 'username'
const PASSWORD_FIELD_NAME = 'password'

const LoginForm = () => {
	const { t, loading, setLoading, isMounted, alert, dispatch, theme } = useDefaultComponentTools()
	const {
		handleSubmit,
		control,
		watch,
		formState: { errors }
	} = useForm({
		defaultValues: {
			[PASSWORD_FIELD_NAME]: '',
			[USERNAME_FIELD_NAME]: ''
		},
		mode: 'all'
	})
	const usernameError = errors[USERNAME_FIELD_NAME]?.message || ''
	const passwordError = errors[PASSWORD_FIELD_NAME]?.message || ''
	const cookies = new Cookies()
	const isSubmitButtonDisabled = !watch([USERNAME_FIELD_NAME]) ||
		!watch(PASSWORD_FIELD_NAME) ||
		usernameError ||
		passwordError ||
		loading
	const onSubmit = async (data) => {
		try {
			setLoading(true)
			const res = await dispatch(signInAction(data, alert))

			if (res.data) {
				let expires = dayjs().add(res.data['expires_in'] || 3600, 's').toDate()
				let key
				for (key in res.data) {
					cookies.set('oauth_' + key, res.data[key],	{expires: expires, path: '/'})
					if (key === 'access_token') {
						const socket = getSocket()
						if (socket.id) {
							socket.emit('disconnectCustom', socket.id)
						}
						dispatch(setToken(res.data[key]))
						dispatch(setShowLoginPopup(false))
					}
				}
			}
		} catch (e) {
			alert.removeAll()
			alert.error(e.message)
		} finally {
			isMounted() && setLoading(false)
		}
	}

	return (
		<FormLoginBody>
			<form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
				<FormControl
					error={!!usernameError}
					sx={{ mb: 1, mt: 1 }}
				>
					<Typography sx={{ mb: 1 }}>
						{t('Username or Email')}
					</Typography>
					<Controller
						control={control}
						rules={{ required: 'Username or Email is required.'}}
						render={({ field }) => (
							<TextField
								id={'login-username'}
								{...field}
								variant='outlined'
								type={'text'}
								autoComplete='off'
								inputProps={{ autoComplete: 'off' }}
								sx={{'.MuiInputBase-input': {fontSize: '12px', p: '12px'},
									background: theme.palette.second,
									border: 0,  borderRadius: 1, 'input': {
										color: theme.palette.text.primary
									}, width: '100%' }}
							/>
						)}
						name={USERNAME_FIELD_NAME}
					/>
					{!!usernameError && (
						<FormHelperText id={'login-username'}>
							{usernameError}
						</FormHelperText>
					)}
				</FormControl>
				<FormControl
					margin={'dense'}
					error={!!passwordError}
				>
					<Typography sx={{ mb: 1 }}>
						{t('Password')}
					</Typography>
					<Controller
						control={control}
						rules={{ required: 'Password is required.'}}
						render={({ field }) => (
							<TextField
								{...field}
								type={'password'}
								id={'login-password'}
								autoComplete='off'
								inputProps={{ autoComplete: 'off' }}
								sx={{'.MuiInputBase-input': {fontSize: '12px', p: '12px'},
									background: theme.palette.second,
									border: 0,  borderRadius: 1, 'input': {
										color: theme.palette.text.primary
									},  width: '100%' }}
							/>
						)}
						name={PASSWORD_FIELD_NAME}
					/>

					{!!passwordError && (
						<FormHelperText id={'login-password'}>
							{passwordError}
						</FormHelperText>
					)}
				</FormControl>
				<FormControlLabel
					control={
						<Checkbox />
					}
					label={t('Remember me')}
				/>
				<ForgotLink
					href={getLangLink('/forgot-password/', i18n.language.toLowerCase())}
					color={'primary'}
					maxWidth={'11rem'}
					sx={{cursor: 'pointer', mb: 5 }}
				>
					{t('Forgot your password')}
				</ForgotLink>
				<AppButton
					type={'submit'}
					disabled={isSubmitButtonDisabled}
					loading={loading}
					title={t('Sign In')}
				/>
			</form>
		</FormLoginBody>
	)
}

export default LoginForm
